import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { MusicIcon, ConfirmationModal, useConfirmation } from '../../components/shared';
import { useTranslation } from 'react-i18next';

const Tracks: React.FC = () => {
  const { user } = useAuth();
  const { releases, withdrawApproval, submitForApproval, isLoading } = useData();
  const [activeFilter, setActiveFilter] = useState<'all' | 'published' | 'pending' | 'draft' | 'rejected'>('all');
  const { t } = useTranslation();
  const { confirmationState, showConfirmation, handleCancel } = useConfirmation();

  const userReleases = releases.filter(release => release.artist_id === user?.id);
  
  const filteredReleases = userReleases.filter(release => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'published') return release.status === 'published' || release.status === 'approved';
    if (activeFilter === 'pending') return release.status === 'pending' || release.status === 'submitted';
    return release.status === activeFilter;
  });

  const filterCounts = {
    all: userReleases.length,
    published: userReleases.filter(r => r.status === 'published' || r.status === 'approved').length,
    pending: userReleases.filter(r => r.status === 'pending' || r.status === 'submitted').length,
    draft: userReleases.filter(r => r.status === 'draft').length,
    rejected: userReleases.filter(r => r.status === 'rejected').length
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
      case 'approved':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'pending':
      case 'submitted':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'draft':
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'published':
        return t('status.published');
      case 'approved':
        return t('status.approved');
      case 'pending':
        return t('status.pending');
      case 'submitted':
        return t('status.submitted');
      case 'draft':
        return t('status.draft');
      case 'rejected':
        return t('status.rejected');
      default:
        return status;
    }
  };

  const handleWithdrawApproval = async (releaseId: string) => {
    const confirmed = await showConfirmation({
      title: t('tracks.withdrawConfirmTitle'),
      message: t('tracks.withdrawConfirmMessage'),
      confirmText: t('tracks.withdrawAction'),
      type: 'warning'
    });

    if (confirmed) {
      withdrawApproval(releaseId);
    }
  };

  const handleSubmitForApproval = async (releaseId: string) => {
    const confirmed = await showConfirmation({
      title: t('tracks.submitConfirmTitle'),
      message: t('tracks.submitConfirmMessage'),
      confirmText: t('tracks.submitForApproval'),
      type: 'info'
    });

    if (confirmed) {
      submitForApproval(releaseId);
    }
  };

  const renderActionButtons = (release: any) => {
    const buttons = [];

    // Her zaman görüntüle butonu olmalı
    buttons.push(
      <Link 
        key="view" 
        to={`/tracks/view/${release.id}`} 
        className="flex-1 btn-secondary text-sm py-2 text-center"
      >
        {t('common.view')}
      </Link>
    );

    // Status'a göre diğer butonları ekle
    switch (release.status) {
      case 'draft':
        buttons.unshift(
          <Link 
            key="edit" 
            to={`/tracks/edit/${release.id}`} 
            className="flex-1 btn-secondary text-sm py-2 text-center"
          >
            {t('common.edit')}
          </Link>
        );
        buttons.push(
          <button
            key="submit"
            onClick={() => handleSubmitForApproval(release.id)}
            className="flex-1 bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 text-sm py-2 rounded-lg transition-all duration-200"
          >
            {t('common.submitForApproval')}
          </button>
        );
        break;

      case 'pending':
      case 'submitted':
        buttons.unshift(
          <button 
            key="withdraw"
            onClick={() => handleWithdrawApproval(release.id)}
            className="flex-1 bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/30 text-sm py-2 rounded-lg transition-all duration-200"
          >
            {t('common.withdrawApproval')}
          </button>
        );
        break;

      case 'rejected':
        buttons.unshift(
          <Link 
            key="edit" 
            to={`/tracks/edit/${release.id}`} 
            className="flex-1 btn-secondary text-sm py-2 text-center"
          >
            {t('common.edit')}
          </Link>
        );
        buttons.push(
          <button
            key="submit"
            onClick={() => handleSubmitForApproval(release.id)}
            className="flex-1 bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 text-sm py-2 rounded-lg transition-all duration-200"
          >
            {t('common.submitForApproval')}
          </button>
        );
        break;

      case 'published':
      case 'approved':
        // Sadece görüntüle butonu yeterli (zaten eklendi)
        break;

      default:
        // Bilinmeyen status için de görüntüle butonu yeterli
        break;
    }

    return (
      <div className="flex space-x-2">
        {buttons}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">{t('tracks.myTracks')}</h1>
          <p className="mt-1 text-sm text-white/70">
            {t('tracks.subtitle')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/tracks/new"
            className="btn-primary inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('tracks.newRelease')}
          </Link>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-2">
        <nav className="flex flex-wrap gap-2">
          <button 
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
              activeFilter === 'all' 
                ? 'bg-orange-500 text-white shadow-lg' 
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            {t('common.all')} ({filterCounts.all})
          </button>
          <button 
            onClick={() => setActiveFilter('published')}
            className={`px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
              activeFilter === 'published' 
                ? 'bg-orange-500 text-white shadow-lg' 
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            {t('tracks.published')} ({filterCounts.published})
          </button>
          <button 
            onClick={() => setActiveFilter('pending')}
            className={`px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
              activeFilter === 'pending' 
                ? 'bg-orange-500 text-white shadow-lg' 
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            {t('tracks.pending')} ({filterCounts.pending})
          </button>
          <button 
            onClick={() => setActiveFilter('draft')}
            className={`px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
              activeFilter === 'draft' 
                ? 'bg-orange-500 text-white shadow-lg' 
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            {t('tracks.draft')} ({filterCounts.draft})
          </button>
          <button 
            onClick={() => setActiveFilter('rejected')}
            className={`px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
              activeFilter === 'rejected' 
                ? 'bg-orange-500 text-white shadow-lg' 
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            {t('tracks.rejected')} ({filterCounts.rejected})
          </button>
        </nav>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="glass-card animate-pulse">
              <div className="aspect-square bg-white/20 rounded-lg mb-4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-white/20 rounded w-3/4"></div>
                <div className="h-3 bg-white/20 rounded w-1/2"></div>
                <div className="h-3 bg-white/20 rounded w-1/4"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredReleases.length === 0 && (
        <div className="glass-card text-center py-16">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center mb-6">
            <MusicIcon className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
            {activeFilter === 'all' 
              ? t('messages.noTracks') 
              : activeFilter === 'published' ? t('messages.noPublishedTracks') :
                activeFilter === 'pending' ? t('messages.noPendingTracks') :
                activeFilter === 'draft' ? t('messages.noDraftTracks') : t('messages.noRejectedTracks')}
          </h3>
          <p className="text-white/70 mb-8 max-w-md mx-auto">
            {activeFilter === 'all' 
              ? t('messages.uploadFirstTrackMessage') 
              : t('messages.noTracksOfType', { type: activeFilter })}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/tracks/new"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('dashboard.uploadNewTrack')}
            </Link>
            {activeFilter !== 'all' && (
              <button
                onClick={() => setActiveFilter('all')}
                className="px-6 py-3 bg-white/10 text-white rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                Tümünü Görüntüle
              </button>
            )}
          </div>
        </div>
      )}

      {/* Releases Grid */}
      {!isLoading && filteredReleases.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredReleases.map((release) => (
            <div key={release.id} className="glass-card group">
              <div className="relative">
                {release.cover_art ? (
                  <img
                    src={release.cover_art}
                    alt={release.title}
                    className="w-full h-48 object-cover rounded-xl border border-white/20"
                  />
                ) : (
                  <div className="w-full h-48 flex items-center justify-center border border-white/20 rounded-xl">
                    <MusicIcon size="xl" />
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(release.status)}`}>
                    {getStatusText(release.status)}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-white group-hover:text-orange-300 transition-colors duration-200">
                      {release.title}
                    </h3>
                    <p className="text-sm text-white/70">{release.type} • {release.genre}</p>
                    <p className="text-xs text-white/60 mt-1">
                      {new Date(release.release_date).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  <h4 className="text-sm font-medium text-white">{t('admin.songs')}</h4>
                  <div className="max-h-24 overflow-y-auto space-y-1">
                    {release.tracks.map((track, index) => (
                      <div key={track.id} className="text-xs text-white/70 p-2 bg-white/5 rounded-lg">
                        {index + 1}. {track.title} ({track.duration})
                      </div>
                    ))}
                  </div>
                </div>
                
                {renderActionButtons(release)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        onConfirm={confirmationState.onConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default Tracks; 